import { useState, useRef, useCallback } from 'react'
import { Mic, Upload, X, Check, Play, Square, Loader2, RefreshCw } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { VOICE_PRESETS, fullVoiceDescription, getPresetVoicePreviewUrl, getVoiceName } from '@/lib/voicePresets'

interface VoicePickerProps {
  currentVoiceId?: string
  onVoiceSelect: (voiceId: string) => void
  onVoiceClone?: (file: File) => Promise<void>
  onRemoveVoice: () => void
  onPreviewVoice?: (voiceId: string) => Promise<{ audioUrl: string | null }>
  label?: string
  ttsPreviewUrl?: string
  clipsWithTts?: number
  klingVoiceStatus?: 'none' | 'creating' | 'ready' | 'failed'
}

export function VoicePicker({ currentVoiceId, onVoiceSelect, onVoiceClone, onRemoveVoice, onPreviewVoice, label, ttsPreviewUrl, clipsWithTts = 0, klingVoiceStatus }: VoicePickerProps) {
  const [open, setOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [recording, setRecording] = useState(false)
  const [playingVoice, setPlayingVoice] = useState<string | null>(null)
  const [loadingVoice, setLoadingVoice] = useState<string | null>(null)
  const [pendingVoice, setPendingVoice] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const previewCacheRef = useRef<Record<string, string>>({})

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('audio/')) return
    if (!onVoiceClone) return
    setUploading(true)
    try {
      await onVoiceClone(file)
      setOpen(false)
    } finally {
      setUploading(false)
    }
  }

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const file = new File([blob], 'voice-recording.webm', { type: 'audio/webm' })
        handleFileSelect(file)
      }
      mediaRecorderRef.current = recorder
      recorder.start()
      setRecording(true)
    } catch (err) {
      console.error('[VoicePicker] Mic access denied:', err)
    }
  }, [])

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = null
    setRecording(false)
  }, [])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setPlayingVoice(null)
  }, [])

  const playAudioUrl = useCallback((url: string, voiceId: string) => {
    stopAudio()
    const audio = new Audio(url)
    audioRef.current = audio
    audio.onended = () => setPlayingVoice(null)
    audio.onerror = () => setPlayingVoice(null)
    audio.play()
    setPlayingVoice(voiceId)
  }, [stopAudio])

  const handlePreview = useCallback(async (voiceId: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (playingVoice === voiceId) {
      stopAudio()
      return
    }

    // Use existing TTS audio for current voice
    if (voiceId === currentVoiceId && ttsPreviewUrl) {
      playAudioUrl(ttsPreviewUrl, voiceId)
      return
    }

    const presetPreviewUrl = getPresetVoicePreviewUrl(voiceId)
    if (presetPreviewUrl) {
      playAudioUrl(presetPreviewUrl, voiceId)
      return
    }

    // Check cache
    if (previewCacheRef.current[voiceId]) {
      playAudioUrl(previewCacheRef.current[voiceId], voiceId)
      return
    }

    // Generate preview via API
    if (!onPreviewVoice) return
    setLoadingVoice(voiceId)
    try {
      const result = await onPreviewVoice(voiceId)
      if (result.audioUrl) {
        previewCacheRef.current[voiceId] = result.audioUrl
        playAudioUrl(result.audioUrl, voiceId)
      }
    } catch (err) {
      console.error('[VoicePicker] Preview failed:', err)
    } finally {
      setLoadingVoice(null)
    }
  }, [playingVoice, currentVoiceId, ttsPreviewUrl, onPreviewVoice, stopAudio, playAudioUrl])

  const handleVoiceChange = useCallback((voiceId: string) => {
    if (clipsWithTts > 0 && currentVoiceId && voiceId !== currentVoiceId) {
      setPendingVoice(voiceId)
    } else {
      onVoiceSelect(voiceId)
      setOpen(false)
    }
  }, [clipsWithTts, currentVoiceId, onVoiceSelect])

  const confirmVoiceChange = useCallback(() => {
    if (pendingVoice) {
      onVoiceSelect(pendingVoice)
      setPendingVoice(null)
      setOpen(false)
    }
  }, [pendingVoice, onVoiceSelect])

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPendingVoice(null); stopAudio() } }}>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md bg-muted text-secondary-foreground hover:bg-muted/80 hover:text-secondary-foreground transition-colors">
          <Mic size={12} />
          <span>{label || (currentVoiceId ? getVoiceName(currentVoiceId) : 'Set Voice')}</span>
          {klingVoiceStatus === 'creating' && <Loader2 size={10} className="animate-spin text-amber-500" />}
          {klingVoiceStatus === 'ready' && <Check size={10} className="text-success" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        {/* Regen confirmation overlay */}
        {pendingVoice && (
          <div className="px-3 py-3 bg-amber-50 border-b border-amber-200">
            <div className="text-xs font-medium text-amber-800 mb-1">
              Change to {getVoiceName(pendingVoice)}?
            </div>
            <div className="text-[10px] text-amber-700 mb-2">
              {clipsWithTts} clip{clipsWithTts !== 1 ? 's' : ''} will need video regenerated.
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={confirmVoiceChange}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              >
                <RefreshCw size={9} />
                Change & regen
              </button>
              <button
                onClick={() => setPendingVoice(null)}
                className="px-2 py-1 text-[10px] font-medium rounded bg-background text-secondary-foreground hover:bg-muted border border-border transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {onVoiceClone && !pendingVoice && (
          <div className="p-2 space-y-2">
            {uploading ? (
              <div className="flex items-center justify-center gap-2 py-3 px-2 rounded-md border border-dashed border-border bg-secondary">
                <Loader2 size={14} className="animate-spin text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">{recording ? 'Processing...' : 'Uploading...'}</span>
              </div>
            ) : recording ? (
              <button
                onClick={stopRecording}
                className="flex items-center justify-center gap-2 w-full py-3 px-2 rounded-md border border-destructive/30 bg-destructive/5 cursor-pointer transition-colors hover:bg-destructive/10"
              >
                <Square size={12} className="text-destructive animate-pulse" fill="currentColor" />
                <span className="text-[10px] text-destructive font-medium">Stop recording</span>
              </button>
            ) : (
              <div className="flex gap-2">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex-1 flex flex-col items-center gap-1 py-3 px-2 rounded-md border border-dashed cursor-pointer transition-colors',
                    dragOver
                      ? 'border-border/80 bg-muted'
                      : 'border-border hover:border-border/80 hover:bg-accent'
                  )}
                >
                  <Upload size={14} className="text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">Upload sample</span>
                </div>
                <button
                  onClick={startRecording}
                  className="flex-1 flex flex-col items-center gap-1 py-3 px-2 rounded-md border border-dashed border-border hover:border-border/80 hover:bg-accent cursor-pointer transition-colors"
                >
                  <Mic size={14} className="text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">Record</span>
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleFileSelect(file)
              }}
            />
          </div>
        )}

        {!pendingVoice && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="px-3 py-2 text-xs font-medium text-muted-foreground">
              Preset voices
            </DropdownMenuLabel>
            <div className="space-y-0.5 px-2 pb-2">
              {VOICE_PRESETS.map((voice) => {
                const isSelected = currentVoiceId === voice.id
                const isPlaying = playingVoice === voice.id
                const isLoading = loadingVoice === voice.id
                const canPreviewVoice = !!getPresetVoicePreviewUrl(voice.id) || !!onPreviewVoice || (voice.id === currentVoiceId && !!ttsPreviewUrl)
                return (
                  <div
                    key={voice.id}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-md transition-all',
                      isSelected ? 'bg-foreground text-background' : 'hover:bg-muted text-secondary-foreground'
                    )}
                  >
                    {canPreviewVoice && (
                      <button
                        onClick={(e) => handlePreview(voice.id, e)}
                        aria-label={isPlaying ? `Stop ${voice.name} preview` : `Play ${voice.name} preview`}
                        title={isPlaying ? `Stop ${voice.name} preview` : `Play ${voice.name} preview`}
                        className={cn(
                          'w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors',
                          isSelected
                            ? 'bg-white/20 hover:bg-white/30 text-white'
                            : 'bg-muted hover:bg-muted/80 text-secondary-foreground'
                        )}
                      >
                        {isLoading ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : isPlaying ? (
                          <Square size={8} fill="currentColor" />
                        ) : (
                          <Play size={8} fill="currentColor" className="ml-0.5" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleVoiceChange(voice.id)}
                      className="flex-1 text-left min-w-0"
                    >
                      <div className="text-xs font-medium truncate">{voice.name}</div>
                      <div className={cn(
                        'text-[10px] leading-tight',
                        isSelected ? 'text-white/60' : 'text-muted-foreground'
                      )}>
                        {fullVoiceDescription(voice)}
                      </div>
                    </button>
                    {isSelected && <Check size={12} className="text-success shrink-0" />}
                  </div>
                )
              })}
            </div>
          </>
        )}

        {currentVoiceId && !pendingVoice && (
          <>
            <DropdownMenuSeparator />
            <button
              onClick={() => {
                onRemoveVoice()
                setOpen(false)
              }}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs text-destructive hover:bg-destructive/10 transition-colors"
            >
              <X size={12} />
              Remove Voice
            </button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
